import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { retryUnlessRecordUnavailable } from "@/common/utils/record-unavailable-error";
import { useOrganization } from "@/hooks/use-organization";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { queryClient } from "@/ajax/queryClient";

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  userIds: z.array(z.string().uuid()).optional(),
});

type ProviderPoolFormValues = z.infer<typeof formSchema>;

interface ProviderPoolDialogProps {
  poolId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function ProviderPoolEditDialog({
  poolId,
  open,
  onOpenChange,
  onSuccess,
}: ProviderPoolDialogProps) {
  const { selectedOrgBusinessUnit } = useOrganization();
  const trpc = useTRPC();

  const isEditing = !!poolId;
  const title = isEditing ? "Edit Provider Pool" : "Create Provider Pool";
  const actionText = isEditing ? "Save Changes" : "Create Pool";

  const form = useForm<ProviderPoolFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
    },
  });

  // Fetch pool data if editing
  const { isLoading: isLoadingPool, data: pool } = useQuery(
    trpc.providerPool.get.queryOptions(
      {
        id: poolId ?? "",
        organizationBusinessUnitId: selectedOrgBusinessUnit.id,
      },
      {
        enabled: !!poolId,
        // Shares the pool page's query, so it must not reintroduce retries
        // for a pool outside the selected business unit.
        retry: retryUnlessRecordUnavailable,
      },
    ),
  );

  useEffect(() => {
    if (pool) {
      form.reset({
        name: pool.name,
        description: pool.description ?? undefined,
      });
    }
  }, [pool, form]);

  const createMutation = useMutation(
    trpc.providerPool.create.mutationOptions({
      onSuccess: async () => {
        toast.success("Provider pool created successfully");
        await queryClient.invalidateQueries({
          queryKey: trpc.providerPool.list.queryKey({
            organizationBusinessUnitId: selectedOrgBusinessUnit.id,
          }),
        });
        onSuccess();
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }),
  );
  const updateMutation = useMutation(
    trpc.providerPool.update.mutationOptions({
      onSuccess: async () => {
        toast.success("Provider pool updated successfully");
        await queryClient.invalidateQueries({
          queryKey: trpc.providerPool.list.queryKey({
            organizationBusinessUnitId: selectedOrgBusinessUnit.id,
          }),
        });
        onSuccess();
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }),
  );

  const onSubmit = (values: ProviderPoolFormValues) => {
    if (isEditing) {
      updateMutation.mutate({
        id: poolId,
        name: values.name,
        description: values.description,
        organizationBusinessUnitId: selectedOrgBusinessUnit.id,
      });
    } else {
      createMutation.mutate({
        ...values,
        organizationBusinessUnitId: selectedOrgBusinessUnit.id,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Create a pool of providers that can be assigned to shifts.
          </DialogDescription>
        </DialogHeader>

        {isLoadingPool ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter pool name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description (Optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Enter a description for this pool"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    createMutation.isPending || updateMutation.isPending
                  }
                >
                  {createMutation.isPending ||
                    (updateMutation.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ))}
                  {actionText}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
